import React, { useMemo, useState } from "react";
import { CCCombobox, CCTagList } from "@pymblecitizencoders/cc-ui";
/* Moved to utils/ exactly as the note that used to sit here said they would,
   the moment a second caller appeared — the bulk tagger needs the same rules. */
import { normaliseTag, sameTag } from "../../utils/eventTags";

/**
 * The tag field: pick an existing tag, or coin a new one.
 *
 * ## Why this is not `CCMultiSelect`
 *
 * cc-ui's own multi-select is exactly this composition — `CCTagList` over
 * `CCCombobox` — and it was the obvious thing to use. It cannot be, for one
 * reason: it derives its chips with `options.filter(o => values.includes(...))`,
 * so a value with no matching option renders NOTHING. Tags are an open
 * vocabulary, so a tag that is on the event but missing from the catalogue is a
 * normal state rather than a broken one — the catalogue read can fail, and it is
 * one request behind a tag somebody coined a moment ago in another tab. Under
 * CCMultiSelect that tag would vanish from the screen while remaining on the
 * document, and the next save would write back only what the person could see.
 * A field that silently drops data it was handed is the wrong primitive here, so
 * the chips are rendered from `values` and the catalogue only ever ADDS
 * suggestions.
 *
 * ## Async mode is deliberate
 *
 * Passing `onQueryChange` puts CCCombobox in async mode, which turns OFF its own
 * filtering and makes it show the options it is handed. That is what allows the
 * "Add ..." row: the create affordance has to appear precisely when nothing
 * matches, which is the one case a local filter would render as an empty list.
 * The filtering below is therefore ours, and so is the matching rule — the same
 * case-insensitive comparison the API uses, so what the field calls a duplicate
 * and what the server calls a duplicate cannot drift apart.
 */

interface EventTagFieldProps {
	/** The tags on the event, in stored order. */
	values: string[];
	/** Every tag anyone has used, for the type-ahead. May be empty or stale. */
	catalogue: string[];
	disabled?: boolean;
	onChange: (tags: string[]) => void;
	label?: string;
	/** Refuses to add more than this, matching the API's own ceiling. */
	maxTags?: number;
}

/** The API's `MAX_TAGS`. Kept in step by hand; the server is the real gate. */
const DEFAULT_MAX_TAGS = 25;

/**
 * Marks the synthetic "coin a new tag" row.
 *
 * A leading space, which `normaliseTag` strips from anything a person types, so
 * no real tag can collide with the prefix and be mistaken for the create row.
 */
const CREATE_PREFIX = " create:";

export const EventTagField: React.FC<EventTagFieldProps> = ({
	values,
	catalogue,
	disabled,
	onChange,
	label = "Tags",
	maxTags = DEFAULT_MAX_TAGS,
}) => {
	const [query, setQuery] = useState("");

	const full = values.length >= maxTags;

	const options = useMemo(() => {
		const typed = normaliseTag(query);
		const needle = typed.toLocaleLowerCase("en-AU");

		/* Suggestions: catalogue entries not already on the event. Filtered here
		   rather than by CCCombobox because async mode disables its own filter —
		   see the component note. */
		const suggestions = catalogue
			.filter((tag) => !values.some((v) => sameTag(v, tag)))
			.filter(
				(tag) => !needle || tag.toLocaleLowerCase("en-AU").includes(needle),
			)
			.slice(0, 50)
			.map((tag) => ({ value: tag, label: tag }));

		/* The create row, offered only when the typed tag is genuinely new: not
		   already on the event, and not a spelling of something the catalogue
		   already suggests. Without the second test, typing the whole of an
		   existing tag would offer to "add" it beside itself, and picking the
		   wrong one of the two is how a catalogue grows near-duplicates. */
		const isNew =
			typed.length > 0 &&
			!values.some((v) => sameTag(v, typed)) &&
			!catalogue.some((tag) => sameTag(tag, typed));

		return isNew
			? [
					{ value: `${CREATE_PREFIX}${typed}`, label: `Add "${typed}"` },
					...suggestions,
				]
			: suggestions;
	}, [catalogue, query, values]);

	const commit = (chosen: string) => {
		const tag = normaliseTag(
			chosen.startsWith(CREATE_PREFIX)
				? chosen.slice(CREATE_PREFIX.length)
				: chosen,
		);
		setQuery("");
		if (!tag || full) return;
		/* Belt and braces: the options above already exclude what is on the event,
		   but a stale render could still commit one. */
		if (values.some((v) => sameTag(v, tag))) return;
		onChange([...values, tag]);
	};

	const remove = (tag: string) => {
		onChange(values.filter((v) => v !== tag));
	};

	return (
		<div className="em-tag-field">
			<CCCombobox
				label={label}
				fullWidth
				disabled={disabled || full}
				options={options}
				/* Always null: the box is an entry point, not a selection. A value
				   here would make CCCombobox display the last tag added as though
				   it were the field's own content. */
				value={null}
				onChange={(value) => value && commit(value)}
				onQueryChange={setQuery}
				placeholder="Search or add a tag"
				emptyLabel={
					catalogue.length === 0
						? "No tags yet - type one to create it"
						: "No matching tags"
				}
				/* The only hint left is the ceiling, and only once it is reached.
				   The standing "pick one or type a new one" line was removed: the
				   placeholder already says it, and a permanent sentence under a
				   field explaining how a combobox works is a sentence everybody
				   reads once and then reads past forever. */
				hint={full ? `An event can carry at most ${maxTags} tags.` : undefined}
			/>
			{/* BESIDE the box, not above it: the chips are the field's value and the
			    box is how you add to it, so putting the growing half on the right
			    keeps the input anchored in one place instead of walking down the
			    form as tags are added.

			    Chips from `values`, never from the catalogue — see the note at the
			    top of this file on why that distinction is load-bearing. */}
			<div className="em-tag-field__chips">
				<CCTagList
					tags={values.map((tag) => ({ id: tag, label: tag }))}
					onRemove={disabled ? undefined : remove}
					emptyLabel="No tags yet"
				/>
			</div>
		</div>
	);
};
